import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Brand pills (docs/STYLE.md "Components"): full radius, a solid 3px shadow
  // instead of a blurred one, lift 2px on hover and press flat on click.
  "inline-flex items-center justify-center gap-2 font-semibold transition-[transform,background-color,box-shadow,color] duration-200 ease-snap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:pointer-events-none disabled:opacity-50 rounded-button cursor-pointer active:translate-y-px",
  {
    variants: {
      variant: {
        primary: "bg-brand-500 text-white shadow-press hover:-translate-y-0.5 hover:bg-brand-600 active:shadow-none",
        /** The friendly call to action (landing "Get started"): yellow with navy ink. */
        cta: "bg-cta text-ink shadow-press-cta hover:-translate-y-0.5 active:shadow-none",
        secondary: "bg-surface text-text-primary ring-2 ring-inset ring-text-primary hover:-translate-y-0.5 hover:bg-surface-tertiary",
        ghost: "text-text-secondary hover:bg-surface-tertiary hover:text-text-primary",
        danger: "bg-error-600 text-white shadow-[0_3px_0_var(--color-error-700)] hover:-translate-y-0.5 active:shadow-none",
        /** People's action: the human pink, navy ink (white on pink is only 3.3:1). */
        volunteer: "bg-human-500 text-ink shadow-[0_3px_0_var(--color-human-700)] hover:-translate-y-0.5 active:shadow-none",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
